import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Shield,
  Search,
  ChevronLeft,
  ChevronRight,
  UserCog,
  LogIn,
  Trash2,
  Key,
  ArrowRightLeft,
  UserPlus,
  Clock,
  Filter,
  RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

const ACTION_LABELS = {
  login_success: 'Connexion réussie',
  login_failed: 'Connexion échouée',
  account_created: 'Compte créé',
  account_deleted: 'Compte supprimé',
  account_updated: 'Compte modifié',
  permission_change: 'Permissions modifiées',
  role_change: 'Rôle modifié',
  owner_transfer: 'Transfert de propriété',
  password_set: 'Mot de passe défini',
  password_change: 'Mot de passe modifié',
  password_reset_requested: 'Réinitialisation demandée',
  invitation_sent: 'Invitation envoyée',
  owner_set: 'Propriétaire défini',
  scorm_completed: 'Module SCORM terminé',
};

const ACTION_ICONS = {
  login_success: LogIn,
  login_failed: LogIn,
  account_created: UserPlus,
  account_deleted: Trash2,
  account_updated: UserCog,
  permission_change: Shield,
  role_change: ArrowRightLeft,
  owner_transfer: ArrowRightLeft,
  password_set: Key,
  password_change: Key,
  password_reset_requested: Key,
  invitation_sent: UserPlus,
  owner_set: Shield,
  scorm_completed: Shield,
};

const ACTION_COLORS = {
  login_success: 'bg-emerald-100 text-emerald-700',
  login_failed: 'bg-red-100 text-red-700',
  account_created: 'bg-blue-100 text-blue-700',
  account_deleted: 'bg-red-100 text-red-700',
  account_updated: 'bg-amber-100 text-amber-700',
  permission_change: 'bg-purple-100 text-purple-700',
  role_change: 'bg-indigo-100 text-indigo-700',
  owner_transfer: 'bg-orange-100 text-orange-700',
  password_set: 'bg-teal-100 text-teal-700',
  password_change: 'bg-teal-100 text-teal-700',
  password_reset_requested: 'bg-amber-100 text-amber-700',
  invitation_sent: 'bg-cyan-100 text-cyan-700',
  owner_set: 'bg-orange-100 text-orange-700',
  scorm_completed: 'bg-emerald-100 text-emerald-700',
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function AuditLogPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [searchEmail, setSearchEmail] = useState('');
  const [availableActions, setAvailableActions] = useState([]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (actionFilter && actionFilter !== 'all') params.append('action', actionFilter);
      if (searchEmail.trim()) params.append('actor_email', searchEmail.trim());

      const res = await fetch(`${API}/api/audit-log?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching audit logs', err);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, searchEmail]);

  const fetchActions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/audit-log/actions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAvailableActions(data.actions || []);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const renderDetails = (log) => {
    const d = log.details || {};
    const parts = [];
    if (d.old_type && d.new_type) parts.push(`${d.old_type} → ${d.new_type}`);
    if (d.reason) parts.push(d.reason);
    if (d.ip) parts.push(`IP: ${d.ip}`);
    if (d.new_owner_email) parts.push(`Nouveau propriétaire: ${d.new_owner_email}`);
    if (d.changed_categories) parts.push(`Catégories: ${d.changed_categories.join(', ')}`);
    if (d.invited_count) parts.push(`${d.invited_count} invitation(s)`);
    if (parts.length === 0 && log.target_name) parts.push(log.target_name);
    return parts.join(' | ');
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="audit-log-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="h-6 w-6 text-teal-600" />
              Journal d'audit
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Suivi des modifications de permissions, comptes et connexions
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setPage(0); fetchLogs(); }}
            data-testid="audit-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Actualiser
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              <Filter className="h-3 w-3 inline mr-1" />
              Type d'action
            </label>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
              <SelectTrigger data-testid="audit-action-filter">
                <SelectValue placeholder="Toutes les actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                {availableActions.map((a) => (
                  <SelectItem key={a} value={a}>
                    {ACTION_LABELS[a] || a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              <Search className="h-3 w-3 inline mr-1" />
              Email de l'acteur
            </label>
            <Input
              placeholder="Rechercher par email..."
              value={searchEmail}
              onChange={(e) => { setSearchEmail(e.target.value); setPage(0); }}
              data-testid="audit-email-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <ScrollArea className="w-full">
            <table className="w-full text-sm" data-testid="audit-log-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Action</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Acteur</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Cible</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Détails</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-400">
                      Chargement...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-400">
                      Aucune entrée dans le journal d'audit
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const Icon = ACTION_ICONS[log.action] || Shield;
                    const color = ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700';
                    return (
                      <tr
                        key={log.log_id}
                        className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors"
                        data-testid={`audit-row-${log.log_id}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Clock className="h-3.5 w-3.5" />
                            <span className="text-xs">{formatDate(log.created_at)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
                            <Icon className="h-3.5 w-3.5" />
                            {ACTION_LABELS[log.action] || log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-800 font-medium text-xs">{log.actor_name}</div>
                          <div className="text-slate-400 text-xs">{log.actor_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-600 text-xs">{log.target_type}</div>
                          <div className="text-slate-400 text-xs truncate max-w-[150px]">{log.target_name || log.target_id}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-500 text-xs max-w-[250px] truncate" title={renderDetails(log)}>
                            {renderDetails(log)}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </ScrollArea>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/30">
              <span className="text-xs text-slate-500">
                {total} entrée{total > 1 ? 's' : ''} — Page {page + 1}/{totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  data-testid="audit-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="audit-next-page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
